import { Cached } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, Grid, Stack, Menu, MenuItem, Typography, SelectChangeEvent } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import { BiGroup } from 'react-icons/bi';
import { BsPersonLinesFill } from 'react-icons/bs';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { hasDownloadPermissions, IDownloadPermission, amplitude } from 'wisestep-api';
import {
  WsButton,
  WsColors,
  WsDatePicker,
  WsDateTimePicker,
  WsFilterDialog,
  WsSelect,
  WsElementWithPopOver,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';
import { WsSearchFilter } from 'wisestep-website/src/components';

import { accessDeniedNotoficationTxt } from '../../../../components/AccessControl/AccessControl';
import { ActivateFeature } from '../../../../components/ActivateFeature';
import AccountManagerUserFilter from '../../../../components/filters/components/AccountManagerFilter';
import { sortOptions } from '../../../../components/filters/components/AdvanceFilterHelper';
import SubmissionAdvancedFilterFormProps from '../../../../components/filters/models/SubmissionAdvanceFilterFormProps';
import WsSubmissionsAdvanceFilter from '../../../../components/filters/submissionsFilter';
import { useWorkspaceContext } from '../../../../context';
import SubmitCandidate from '../../../../features/conversationWidget/components/SubmitCandidate';
import conversationWidgetStore from '../../../../features/conversationWidget/store/conversationWidgetStore';
import { useCandidateAts, CandidateAtsAccess } from '../../../../hooks/useCandidateAts';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';

import { SubmissionListingSearchParams } from './SubmissionListingSearchParams';

interface SubmissionListingSearchFilterProps {
  onSearch: (SubmissionListingSearchParams: SubmissionListingSearchParams) => void;
  onApplyFilters: (data: SubmissionAdvancedFilterFormProps) => void;
  handleDownload: (type: string) => void;
  defaultValues: SubmissionAdvancedFilterFormProps;
  clearAdvanceFilters: () => void;
  onSort: (event: SelectChangeEvent) => void;
  searchFilterdefaultValues: SubmissionListingSearchParams;
  isStageStatus: boolean;
}

export default function SubmissionListingSearchFilter({
  onSearch,
  onApplyFilters,
  handleDownload,
  defaultValues,
  clearAdvanceFilters,
  onSort,
  searchFilterdefaultValues,
  isStageStatus,
}: SubmissionListingSearchFilterProps) {
  const methods = useForm<SubmissionListingSearchParams & SubmissionAdvancedFilterFormProps>({
    defaultValues: { ...searchFilterdefaultValues, ...defaultValues },
  });
  const { setValue, handleSubmit, watch } = methods;

  const workspace = useWorkspaceContext();
  const [selectedDate, setSelectedDate] = useState<string>();
  const [isDateTime, setDateTime] = useState<boolean>(false);
  const [minDate, setMinDate] = useState<Date>();
  const fromDate = watch('fromDate');
  const toDate = watch('toDate');
  const dateFilterStatus = watch('dateFilterStatus');
  const watchAccountManagers = watch('accountMangers');
  const [datesUnionBy, setDatesUnionBy] = useState<string>('CREATED_OR_UPDATED_BETWEEN');
  const candidateOpenOrClose = watch('candidateStatus');

  const handleCloseDownloadOptions = () => setDownloadAnchor(null);

  const onClickDownload = (type: string) => {
    handleDownload(type);
    handleCloseDownloadOptions();
  };

  const formattedDate = (date: string): string => dayjs(date).format('DD/MM/YYYY');
  const [permissionState, setPermissionState] = useState<boolean>(false);
  const [downloadAnchor, setDownloadAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(downloadAnchor);

  const handleOpenDownloadOptions = (event: React.MouseEvent<HTMLElement>) =>
    setDownloadAnchor(event.currentTarget);
  useEffect(() => {
    const permissionData = async () => {
      const permissionResponse = await hasDownloadPermissions();
      const permissionResponseData: IDownloadPermission = permissionResponse.data;
      setPermissionState(permissionResponseData.CS_DOWNLOAD_CANDIDATE_LIST);
    };

    permissionData();
  }, []);

  const submissionSearchAmplitudeEvent = (name: string, clickedOnName: string) =>
    amplitude(name, {
      clickedOn: clickedOnName,
      workspaceName: workspace.name,
      pageName: 'JobListing',
      planType: workspace.planType,
      interfaceType: 'web',
      pageUrl: window.location.href,
    });

  useEffect(() => {
    fromDate &&
      toDate &&
      setSelectedDate(`${formattedDate(`${fromDate}`)} to ${formattedDate(`${toDate}`)}`);
    setMinDate(fromDate);
    dateFilterStatus && setDatesUnionBy(dateFilterStatus);
  }, [fromDate, toDate, candidateOpenOrClose, dateFilterStatus]);

  const [advancedFilterDrawerOpen, setAdvancedFilterDrawerOpen] = useState<boolean>(false);
  const openAdvancedFilters = () => {
    submissionSearchAmplitudeEvent('Submission_Advance_filters', 'Advance Filters');
    setAdvancedFilterDrawerOpen(true);
  };
  const closeAdvancedFilters = () => setAdvancedFilterDrawerOpen(false);

  const DateFilterStatusOptions = [
    {
      label: 'Submitted Between',
      value: 'CREATED_BETWEEN',
    },
    {
      label: 'Updated Between',
      value: 'UPDATED_BETWEEN',
    },
    {
      label: 'Submitted Or Updated Between',
      value: 'CREATED_OR_UPDATED_BETWEEN',
    },
  ];

  const mapDatesUnionBy = new Map();
  mapDatesUnionBy.set('CREATED_BETWEEN', 'Submitted Between');
  mapDatesUnionBy.set('UPDATED_BETWEEN', 'Updated Between');
  mapDatesUnionBy.set('CREATED_OR_UPDATED_BETWEEN', 'Submitted Or Updated Between');

  const clearAllFields = () => {
    setSelectedDate('Till Current');
    clearAdvanceFilters();
    methods.reset({ ...searchFilterdefaultValues, ...defaultValues });
  };

  const isViewDownload = useCandidateAts(CandidateAtsAccess.SUBMISSIONS_DOWNLOAD);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const isSubmitCandidate = useCandidateAts(CandidateAtsAccess.SUBMIT_CANDIDATE);

  const selectedJob = conversationWidgetStore((state) => state.selectedJob);

  const [openSubmitCandidate, setOpenSubmitCandidate] = useState(false);

  return (
    <>
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={handleSubmit((data: SubmissionListingSearchParams) => onSearch(data))}
        >
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            <WsSearchFilter
              name="searchFilter"
              showDefaultRightActions={true}
              showLeftActions={true}
              showChildren={true}
              onClickFilter={openAdvancedFilters}
              sortOptions={sortOptions}
              sortDefaultValue={searchFilterdefaultValues.sort}
              onSort={onSort}
              searchPlaceholder="Search for Candidate, Skill, Job Title, Location"
              clearAllFields={clearAllFields}
              leftActionsChildren={
                <Grid
                  container
                  item
                  pt={4}
                  sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
                >
                  <Grid item sx={{ display: 'flex' }}>
                    <WsButton
                      variant="text"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        if (isSubmitCandidate) {
                          setOpenSubmitCandidate(true);
                        } else {
                          setNotification(warning(accessDeniedNotoficationTxt));
                        }
                      }}
                    >
                      Add New Submission
                    </WsButton>
                  </Grid>
                  <Grid item sx={{ display: 'flex' }}>
                    <WsButton variant="text" startIcon={<Cached />} type="submit">
                      Refresh
                    </WsButton>
                    {permissionState &&
                      (workspace?.owns || workspace?.roles?.indexOf('ROLE_DOWNLOAD') !== -1) &&
                      isViewDownload && (
                        <ActivateFeature feature="CS_DOWNLOAD_CANDIDATE_LIST">
                          <WsButton
                            onClick={handleOpenDownloadOptions}
                            startIcon={<FileDownloadOutlinedIcon />}
                            variant="text"
                          >
                            Download
                          </WsButton>
                        </ActivateFeature>
                      )}
                    <Menu
                      anchorEl={downloadAnchor}
                      open={open}
                      onClose={handleCloseDownloadOptions}
                    >
                      <MenuItem onClick={() => onClickDownload('csv')}>Download as CSV</MenuItem>
                      <MenuItem onClick={() => onClickDownload('xls')}>Download as XLS</MenuItem>
                    </Menu>
                  </Grid>
                </Grid>
              }
            >
              <Grid className="submission-filters" container item xs={12} pt={1}>
                <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                  <Grid item xs={4} pt={3}>
                    <WsFilterDialog
                      label="Account Manager"
                      name={
                        watchAccountManagers && watchAccountManagers?.length > 0
                          ? `selected ${watchAccountManagers.length}`
                          : 'All'
                      }
                      icon={<BiGroup size={26} color={WsColors.TheBlues500} />}
                      onClick={(source) =>
                        submissionSearchAmplitudeEvent(
                          source != 'onFilterApply'
                            ? 'Submission_Smartfilters_Account_Manager'
                            : 'Submission_Smartfilters_Account_Manager_Done',
                          source != 'onFilterApply'
                            ? 'Submission_Smartfilters_Account_Manager'
                            : 'Submission_Smartfilters_Account_Manager_Done'
                        )
                      }
                    >
                      <Grid px={1} py={2} item xs={4} minWidth={480}>
                        <AccountManagerUserFilter
                          name="accountMangers"
                          multiple={true}
                          isBorderStyle={true}
                        />
                      </Grid>
                    </WsFilterDialog>
                  </Grid>
                </ErrorBoundary>
                <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                  <Grid item xs={4} pt={3}>
                    <WsFilterDialog
                      name={selectedDate ?? 'Till Current'}
                      icon={<FaRegCalendarAlt size={21} color={WsColors.TheBlues500} />}
                      label={mapDatesUnionBy.get(datesUnionBy)}
                    >
                      <Box p={1}>
                        <ErrorBoundary
                          FallbackComponent={({ error }) => <ErrorLogger error={error} />}
                        >
                          <Box pl={1} pt={1}>
                            <WsSelect
                              name="dateFilterStatus"
                              label="Filter By"
                              selectArray={DateFilterStatusOptions}
                              defaultValue="CREATED_OR_UPDATED_BETWEEN"
                            />
                          </Box>
                        </ErrorBoundary>
                        <ErrorBoundary
                          FallbackComponent={({ error }) => <ErrorLogger error={error} />}
                        >
                          {!isDateTime && (
                            <Stack spacing={2} direction="row" pt={4} pl={1}>
                              <WsDatePicker
                                name="fromDate"
                                label="From Date"
                                disableFuture={true}
                              />
                              <WsDatePicker
                                name="toDate"
                                label="To Date"
                                disableFuture={true}
                                minDate={minDate}
                              />
                            </Stack>
                          )}
                        </ErrorBoundary>
                        <ErrorBoundary
                          FallbackComponent={({ error }) => <ErrorLogger error={error} />}
                        >
                          {isDateTime && (
                            <Stack spacing={2} direction="row" pt={4} pl={1}>
                              <WsDateTimePicker
                                name="fromDate"
                                label="From Date"
                                disableFuture={true}
                              />
                              <WsDateTimePicker
                                name="toDate"
                                label="To Date"
                                disableFuture={true}
                                minDate={minDate}
                              />
                            </Stack>
                          )}
                        </ErrorBoundary>
                        {!isDateTime && (
                          <Box px={1}>
                            <WsButton variant="text" size="small" onClick={() => setDateTime(true)}>
                              select DateTime
                            </WsButton>
                          </Box>
                        )}
                        {isDateTime && (
                          <Box px={1}>
                            <WsButton
                              variant="text"
                              size="small"
                              onClick={() => setDateTime(false)}
                            >
                              select Date
                            </WsButton>
                          </Box>
                        )}
                      </Box>
                    </WsFilterDialog>
                  </Grid>
                </ErrorBoundary>
                <Grid item xs={4} pt={3} container>
                  <Box pt={1.5}>
                    <BsPersonLinesFill size={23} color={WsColors.TheBlues500} />
                  </Box>
                  <Grid pl={2} item xs={10} minWidth={210}>
                    <Typography
                      color={WsColors.TertiaryShades600}
                      fontSize="10px"
                      fontWeight="bold"
                    >
                      Candidate Status
                    </Typography>
                    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                      <Box>
                        <WsElementWithPopOver
                          element={
                            <WsTypography variant={WsTypoVariants.BODY2} cursor="pointer">
                              {watch('candidateStatus') ??
                                searchFilterdefaultValues.candidateStatus ??
                                'Candidate Status'}
                            </WsTypography>
                          }
                        >
                          <Grid rowGap={3}>
                            <Box
                              p={1}
                              sx={{
                                '&:hover': {
                                  background: WsColors.TheBlues500,
                                },
                              }}
                            >
                              <WsTypography
                                onClick={() => {
                                  submissionSearchAmplitudeEvent(
                                    'Submission_Smartfilters_Candidate_status_Active',
                                    'Active'
                                  );
                                  setValue('candidateStatus', 'Active');
                                }}
                                variant={WsTypoVariants.SUB_TITLE}
                                cursor="pointer"
                              >
                                Active
                              </WsTypography>
                            </Box>
                            <Box
                              p={1}
                              sx={{
                                '&:hover': {
                                  background: WsColors.TheBlues500,
                                },
                              }}
                            >
                              <WsTypography
                                onClick={() => {
                                  submissionSearchAmplitudeEvent(
                                    'Submission_Smartfilters_Candidate_status_Closed',
                                    'Closed'
                                  );
                                  setValue('candidateStatus', 'Inactive');
                                }}
                                variant={WsTypoVariants.SUB_TITLE}
                                cursor="pointer"
                              >
                                Closed
                              </WsTypography>
                            </Box>
                            <Box
                              p={1}
                              sx={{
                                '&:hover': {
                                  background: WsColors.TheBlues500,
                                },
                              }}
                            >
                              <WsTypography
                                onClick={() => {
                                  submissionSearchAmplitudeEvent(
                                    'Submission_Smartfilters_Candidate_status_open_and_close',
                                    'Open And Close'
                                  );
                                  setValue('candidateStatus', null);
                                }}
                                variant={WsTypoVariants.SUB_TITLE}
                                cursor="pointer"
                              >
                                Open And Closed
                              </WsTypography>
                            </Box>
                          </Grid>
                        </WsElementWithPopOver>
                      </Box>
                    </ErrorBoundary>
                  </Grid>
                </Grid>
              </Grid>
            </WsSearchFilter>
          </ErrorBoundary>
        </Box>
      </FormProvider>
      {advancedFilterDrawerOpen && (
        <WsSubmissionsAdvanceFilter
          defaultValues={defaultValues}
          open={advancedFilterDrawerOpen}
          onApplyFilters={onApplyFilters}
          filterClose={closeAdvancedFilters}
          isStageStatus={isStageStatus}
        />
      )}

      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        {openSubmitCandidate && (
          <SubmitCandidate
            candidate={null}
            job={selectedJob}
            close={() => setOpenSubmitCandidate(false)}
            openSubmitCandidate={openSubmitCandidate}
          />
        )}
      </ErrorBoundary>
    </>
  );
}
